import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, X, ArrowLeft, Loader2, Save, Search, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { masterDataService } from '../../services/masterDataService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import SearchSelect from '../../components/SearchSelect';
import GridPager from '../../components/GridPager';
import { FilterPopover } from '../../components/gridFilters';
import PickerPopover from '../../components/PickerPopover';
import { exportMasterTemplate, parseMasterTemplate } from '../../utils/masterDataExcel';

// =============================================================================
// MasterDataPage — add / edit / delete for one md_* table.
//
// ONE page serves sites, GL accounts, cost centres, stock codes and warehouses:
// the column list, labels, required flags and enum choices all arrive from the
// server (GET /api/master-data/specs). Adding a sixth table is a block of
// config in masterDataController.js and nothing here.
//
// Same shape as Revenue > Customers — inline grid editing, an Add dialog, a ✕
// per row, and the same Import / Export Excel round-trip — but built on
// MantleDataGrid rather than the legacy MantleGrid adapter, per the house rule
// for new pages.
//
// Edits are held locally and written on Save so a run of changes is one pass,
// not a request per keystroke. Delete and Add go straight through, because
// both are single deliberate acts.
//
// Editing by hand and loading a file are ONE page, not two. Splitting them put
// the same table behind two entries that had to be found separately, and the
// file route is usually reached by exporting what is already there — which
// means starting on this page anyway.
// =============================================================================

// Bulk-load modes. Deliberately the same two, with the same wording, as the
// customer master: anything else would be a second thing to learn.
const IMPORT_MODES = [
  { id: 'overwrite', label: 'Overwrite', hint: 'Update matching records by code, and add any new ones. Records not in the file are left alone.' },
  { id: 'replace', label: 'Replace all', hint: 'Wipe every existing record first, then load the file. Refused if anything else still references them.' },
];

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 '
  + 'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

const widthFor = (col) => {
  if (col.type === 'bool') return 96;
  if (col.type === 'ref') return 190;   // shows "CODE · description"
  if (col.type === 'enum') return 150;
  if (col.type === 'number') return 130;
  if (col.maxLength && col.maxLength > 200) return 300;
  return 170;
};

// ---- Add dialog -------------------------------------------------------------
const AddModal = ({ spec, existing, refOptions = {}, onAdd, onClose, busy }) => {
  const blank = useMemo(() => Object.fromEntries(spec.columns.map((c) => [
    c.id, c.type === 'bool' ? (c.default ?? false) : '',
  ])), [spec]);
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const keyVal = String(form[spec.key] || '').trim().toUpperCase();
  const dup = existing.some((r) => String(r[spec.key]).toUpperCase() === keyVal);
  const missing = spec.columns.find((c) => c.required && c.type !== 'bool' && !String(form[c.id] || '').trim());
  const problem = !keyVal ? `${spec.columns[0].label} is required.`
    : dup ? `That ${spec.singular} code already exists.`
    : missing ? `${missing.label} is required.` : '';

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="w-full max-w-lg max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add {spec.singular}</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">{spec.blurb}</p>
          </div>
          <button type="button" onClick={onClose} disabled={busy}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-2 gap-3">
          {spec.columns.map((col) => (
            <label key={col.id}
              className={`flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400 ${col.type === 'bool' ? '' : 'col-span-2 sm:col-span-1'}`}>
              <span>
                {col.label}
                {col.required && <span className="text-red-600 dark:text-red-400"> *</span>}
              </span>
              {col.type === 'bool' ? (
                <span className="flex items-center gap-2 pt-1.5 text-slate-700 dark:text-slate-200">
                  <input type="checkbox" className="accent-slate-900" checked={!!form[col.id]}
                    onChange={(e) => set(col.id, e.target.checked)} />
                  {form[col.id] ? 'Yes' : 'No'}
                </span>
              ) : col.type === 'enum' ? (
                <SearchSelect
                  value={form[col.id]} onChange={(v) => set(col.id, v)}
                  options={col.allowed.map((a) => ({ code: a }))}
                  clearable={!col.required} placeholder="Choose…" />
              ) : col.type === 'ref' ? (
                // Same control as an enum — the difference is only where the
                // options come from (another master table, loaded by the page).
                <SearchSelect
                  value={form[col.id]} onChange={(v) => set(col.id, v)}
                  options={(refOptions[col.id] || []).map((o) => ({ code: o.code, name: o.description }))}
                  clearable={!col.required}
                  placeholder={(refOptions[col.id] || []).length ? 'Choose…' : 'Nothing to choose from'} />
              ) : col.type === 'number' ? (
                <input type="number" step="any" className={inputClass}
                  value={form[col.id]}
                  onChange={(e) => set(col.id, e.target.value)} />
              ) : (
                <input className={`${inputClass} ${col.upper ? 'font-mono uppercase' : ''}`}
                  value={form[col.id]} maxLength={col.maxLength}
                  onChange={(e) => set(col.id, e.target.value)} />
              )}
            </label>
          ))}
        </div>

        <div className="px-5 pb-5 flex items-center justify-between gap-3">
          <p className="m-0 text-xs text-red-600 dark:text-red-400">{problem}</p>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancel
            </button>
            <button type="button" disabled={!!problem || busy}
              onClick={() => onAdd({ ...form, [spec.key]: keyVal })}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white ${
                problem || busy ? 'bg-slate-400 dark:bg-slate-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Import / Export dialog -------------------------------------------------
// Same layout and wording as the customer master's PortModal, with one addition
// it needs and that one does not: this grid is server-filtered and server-paged,
// so the export can quietly be a subset. It says so rather than letting someone
// export 100 of 4,000 rows and re-import them as "Replace all".
const PortModal = ({
  spec, mode, setMode, isPorting, importErrors, partial, rowCount, total,
  onExport, onImport, onClose,
}) => (
  <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
    onMouseDown={(e) => { if (e.target === e.currentTarget && !isPorting) onClose(); }}>
    <div className="w-full max-w-2xl max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Import / Export — {spec.label}</h3>
        <button type="button" onClick={onClose} disabled={isPorting}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5">
        <h4 className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-2 uppercase tracking-wide">On import, existing records should be…</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {IMPORT_MODES.map((m) => (
            <label key={m.id}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                mode === m.id ? 'border-slate-900 bg-slate-50 dark:bg-slate-700' : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
              }`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="portMode" className="accent-slate-900" checked={mode === m.id} onChange={() => setMode(m.id)} />
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{m.label}</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">{m.hint}</p>
            </label>
          ))}
        </div>

        {partial && (
          <div className="mb-4 p-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              <b>The export covers the {rowCount} record{rowCount === 1 ? '' : 's'} on screen</b>
              {total ? `, not all ${total}` : ''} — a filter, a search or the page size is narrowing it.
              Clear those first if you want the whole table, and be careful with <b>Replace all</b>:
              it would delete everything the file leaves out.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Excel pre-filled with the current records. Choice and yes/no columns carry validation lists.
            </p>
            <button onClick={onExport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 disabled:opacity-50">
              <ArrowDownToLine className="w-3.5 h-3.5" />
              {isPorting ? 'Working…' : 'Export'}
            </button>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Upload a completed template. Every row is validated; if anything is off, nothing is saved.
            </p>
            <button onClick={onImport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
              <ArrowUpFromLine className="w-3.5 h-3.5" />
              {isPorting ? 'Working…' : `Import (${IMPORT_MODES.find((m) => m.id === mode).label})`}
            </button>
          </div>
        </div>

        {importErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="m-0 text-sm font-medium text-red-700 dark:text-red-300 mb-1.5">
              {importErrors.length} row(s) need attention — nothing was imported:
            </p>
            <ul className="text-xs text-red-600 dark:text-red-400 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
              {importErrors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---- Page -------------------------------------------------------------------
const MasterDataPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();

  const [specs, setSpecs] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});          // { code: { field: value } }
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  // Options for every `ref` column — the codes of the table it points at, keyed
  // by column id: { incoterms: [{ code, description }], … }. Loaded once per
  // spec; these lists are a dozen rows each.
  const [refOptions, setRefOptions] = useState({});

  // Which ref cell has its picker open — { colId, rowKey, x, y }.
  const [refPicker, setRefPicker] = useState(null);

  // Import / Export (the Excel round-trip, same as Revenue > Customers).
  const [portOpen, setPortOpen] = useState(false);
  const [portMode, setPortMode] = useState('overwrite');
  const [isPorting, setIsPorting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Server-side filtering and paging. md_stock_codes is why: an item master of
  // a hundred thousand SKUs was previously shipped to the browser in full every
  // time this page opened.
  const [filters, setFilters] = useState({});
  const [filterUI, setFilterUI] = useState(null);   // { colId, options, x, y }
  const [pageInfo, setPageInfo] = useState({ total: null, hasMore: false });
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(100);

  const activeIds = useMemo(() => Object.keys(filters), [filters]);
  // Filtering or searching always returns to page 1 — a filter matching three
  // rows, applied while on page 5, would otherwise look like it found nothing.
  const setFilter = useCallback((colId, f) => {
    setOffset(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (!f || (!f.text && !f.values)) delete next[colId];
      else next[colId] = f;
      return next;
    });
  }, []);

  const spec = specs?.[type] || null;
  // Where the back link goes. Most md_ tables are reached from Master Data, but
  // a table owned by one module (suppliers -> Purchase) declares its own `home`
  // in the spec so the page returns to where the user actually came from.
  const home = spec?.home || { path: '/imports', label: 'Master Data' };
  const rowsRef = useRef([]);

  useEffect(() => {
    let alive = true;
    masterDataService.getSpecs()
      .then((r) => { if (alive && r?.success) setSpecs(r.data); })
      .catch(() => { if (alive) setError('Could not load the master-data definitions.'); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    if (!type) return;
    setLoading(true); setError('');
    try {
      const r = await masterDataService.list(type, { filters, q: q.trim(), limit, offset });
      setRows(r?.success ? r.data : []);
      setPageInfo({ total: r?.total ?? null, hasMore: !!r?.hasMore });
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load these records.');
      setRows([]);
      setPageInfo({ total: null, hasMore: false });
    } finally { setLoading(false); }
  }, [type, filters, q, limit, offset]);

  // Debounced so the search box does not fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Load the lookup lists behind any `ref` columns. `spec.ref` is a key into the
  // same MASTER_SPECS map, so this reuses the ordinary list endpoint rather than
  // needing one of its own — and the referenced table can never come from user
  // input. Reset first, so switching table never shows the previous one's codes.
  useEffect(() => {
    const refCols = (spec?.columns || []).filter((c) => c.type === 'ref' && c.ref);
    setRefOptions({});
    setRefPicker(null);
    if (refCols.length === 0) return undefined;

    let alive = true;
    Promise.all(refCols.map(async (c) => {
      const target = specs?.[c.ref];
      if (!target) return [c.id, []];
      try {
        // These lists are short by nature; 500 is a ceiling, not a page size.
        const r = await masterDataService.list(c.ref, { limit: 500 });
        const opts = (r?.success ? r.data : [])
          // A retired code stays readable on the rows already using it, but is
          // not offered for new ones.
          .filter((x) => x.is_active !== false)
          .map((x) => ({ code: String(x[target.key]), description: x.description || '' }));
        return [c.id, opts];
      } catch { return [c.id, []]; }
    })).then((pairs) => {
      if (alive) setRefOptions(Object.fromEntries(pairs));
    });
    return () => { alive = false; };
  }, [spec, specs]);

  // "NET30 · 30 days from invoice" — the code alone is not self-explanatory,
  // and the description alone is not what gets stored.
  const refLabel = useCallback((colId, code) => {
    if (!code) return '';
    const hit = (refOptions[colId] || []).find((o) => o.code === code);
    return hit && hit.description ? `${code} · ${hit.description}` : String(code);
  }, [refOptions]);

  // The server already filtered and searched — these ARE the matching rows for
  // this page. The alias keeps the rest of the file reading unchanged.
  const filtered = rows;
  rowsRef.current = filtered;

  const columns = useMemo(() => {
    if (!spec) return [];
    const dot = (c) => (activeIds.includes(c.id) ? `${c.label} ●` : c.label);
    return [
      ...spec.columns.map((c) => ({ id: c.id, title: dot(c), width: widthFor(c), hasMenu: true })),
      { id: '__del', title: '', width: 44 },
    ];
  }, [spec, activeIds]);
  const colIds = useMemo(() => columns.map((c) => c.id), [columns]);

  // Local edit wins over the loaded value until Save.
  const valueOf = useCallback((row, field) => {
    const patch = dirty[row?.[spec.key]];
    return patch && Object.prototype.hasOwnProperty.call(patch, field) ? patch[field] : row?.[field];
  }, [dirty, spec]);

  const getCellContent = useCallback(([col, rowIdx]) => {
    const id = colIds[col];
    const r = rowsRef.current[rowIdx];
    if (!r || !spec) return gcell.text('', { readonly: true });
    if (id === '__del') return gcell.text('✕', { readonly: true, theme: { textDark: '#94a3b8' } });

    const c = spec.columns.find((x) => x.id === id);
    const v = valueOf(r, id);
    // The key is the identity of the row and is referenced elsewhere, so it is
    // shown but never editable. Booleans and enums are click-actions.
    if (c.id === spec.key) return gcell.text(String(v ?? ''), { readonly: true });
    if (c.type === 'bool') return gcell.boolean(!!v);
    if (c.type === 'enum') return gcell.text(String(v ?? ''), { readonly: true });
    // Ref: read-only cell, picker on click. Typing free text here would let a
    // code through that the foreign key then rejects on save — better to make
    // the wrong value unreachable than to explain it afterwards.
    if (c.type === 'ref') {
      return v
        ? gcell.text(refLabel(id, v), { readonly: true })
        : gcell.text('— choose —', { readonly: true, theme: { textDark: '#94a3b8' } });
    }
    // An unset number shows blank, not 0 — "no conversion defined" and "a
    // factor of zero" are different facts, and only one of them is valid.
    if (c.type === 'number') {
      return v === null || v === undefined || v === ''
        ? gcell.number(0, { display: '' })
        : gcell.number(Number(v), { display: String(v) });
    }
    return gcell.text(String(v ?? ''));
  }, [colIds, spec, valueOf, refLabel]);

  const patch = (code, field, value) => {
    setDirty((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), [field]: value } }));
    setNote('');
  };

  const onCellEdited = useCallback(([col, rowIdx], newValue) => {
    const id = colIds[col];
    const r = rowsRef.current[rowIdx];
    if (!r || !spec || id === '__del' || id === spec.key) return;
    const c = spec.columns.find((x) => x.id === id);
    if (!c || c.type === 'bool' || c.type === 'enum' || c.type === 'ref') return;
    if (c.type === 'number') {
      // Clearing the cell means "no value", which the server stores as NULL.
      const d = newValue.data;
      patch(r[spec.key], id, d === undefined || d === null || d === '' ? '' : Number(d));
      return;
    }
    patch(r[spec.key], id, newValue.data ?? '');
  }, [colIds, spec]);

  const removeRow = useCallback(async (r) => {
    if (!window.confirm(`Delete ${spec.singular} "${r[spec.key]}"?`)) return;
    setError('');
    try {
      await masterDataService.remove(type, r[spec.key]);
      setRows((prev) => prev.filter((x) => x[spec.key] !== r[spec.key]));
      setNote(`Deleted ${r[spec.key]}.`);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not delete this record.');
    }
  }, [spec, type]);

  const onCellClicked = useCallback(([col, rowIdx], event) => {
    const id = colIds[col];
    const r = rowsRef.current[rowIdx];
    if (!r || !spec) return;
    if (id === '__del') { removeRow(r); return; }
    const c = spec.columns.find((x) => x.id === id);
    if (c?.type === 'bool') { patch(r[spec.key], id, !valueOf(r, id)); return; }
    if (c?.type === 'enum') {
      // Cycle rather than open a popover: these lists are 2–5 items, and one
      // click per step beats a dialog.
      const list = c.required ? c.allowed : ['', ...c.allowed];
      const cur = String(valueOf(r, id) ?? '');
      patch(r[spec.key], id, list[(list.indexOf(cur) + 1) % list.length]);
      return;
    }
    // Ref lists are too long to cycle and their codes are not self-explanatory,
    // so they open the type-to-filter picker anchored under the cell.
    if (c?.type === 'ref') {
      const b = event?.bounds;
      setRefPicker({
        colId: id,
        rowKey: r[spec.key],
        x: b ? b.x : 200,
        y: b ? b.y + b.height : 200,
      });
    }
  }, [colIds, spec, valueOf, removeRow]);

  // Facets are fetched when a column's menu is opened, not with every page.
  // A checklist of a hundred thousand stock codes would be neither cheap to
  // build nor any use; one of stock_group is both — and the server is the only
  // place that knows which a column is.
  const onHeaderMenu = useCallback(async (colIndex, bounds) => {
    const id = colIds[colIndex];
    if (!spec || id === '__del') return;
    const pos = { x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 };
    setFilterUI({ colId: id, options: [], ...pos });   // open immediately, fill when it lands
    try {
      const r = await masterDataService.facets(type, id);
      if (r?.success) setFilterUI((prev) => (prev && prev.colId === id ? { ...prev, options: r.data } : prev));
    } catch { /* contains-box still works with an empty checklist */ }
  }, [colIds, spec, type]);

  const dirtyCount = Object.keys(dirty).length;

  const save = async () => {
    if (!dirtyCount || saving) return;
    setSaving(true); setError(''); setNote('');
    const failures = [];
    for (const [code, fields] of Object.entries(dirty)) {
      try {
        await masterDataService.update(type, code, fields);
      } catch (e) {
        failures.push(`${code}: ${e.response?.data?.message || e.message}`);
      }
    }
    setSaving(false);
    if (failures.length) {
      setError(`${failures.length} record(s) failed — ${failures[0]}`);
    } else {
      setNote(`Saved ${dirtyCount} record${dirtyCount === 1 ? '' : 's'}.`);
    }
    await load();   // reload either way, so the grid shows what is really stored
  };

  const add = async (row) => {
    setAdding(true); setError('');
    try {
      await masterDataService.create(type, row);
      setAddOpen(false);
      setNote(`Added ${row[spec.key]}.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not add this record.');
    } finally { setAdding(false); }
  };

  // ---- Import / Export -----------------------------------------------------
  // The export writes whatever the grid is currently holding. Because this grid
  // is server-filtered and server-paged that can be a subset, which the dialog
  // warns about — silently exporting page 1 of 40 and re-importing it as
  // "Replace all" would delete the other 39.
  const partialExport = activeIds.length > 0 || !!q.trim()
    || (pageInfo.total != null && rows.length < pageInfo.total);

  const handleExport = async () => {
    setImportErrors([]); setIsPorting(true); setError('');
    try {
      await exportMasterTemplate({ spec, rows, refOptions });
      setNote(`Exported ${rows.length} ${spec.label.toLowerCase()}.`);
    } catch {
      setError('Could not generate the Excel template.');
    } finally { setIsPorting(false); }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // so re-picking the same file fires onChange again
    if (!file) return;
    setImportErrors([]); setError(''); setNote(''); setIsPorting(true);
    try {
      const { rows: parsed, errors } = await parseMasterTemplate(file, spec, refOptions);
      if (errors.length) { setImportErrors(errors); return; }
      if (parsed.length === 0 && portMode !== 'replace') {
        setError('No data rows found in the template.');
        return;
      }
      if (portMode === 'replace'
        && !window.confirm(
          `Replace ALL ${spec.label.toLowerCase()} with the ${parsed.length} record(s) in this file?\n\n`
          + 'Everything not in the file is deleted.'
        )) return;

      const res = await masterDataService.bulkUpsert(type, { mode: portMode, rows: parsed });
      setPortOpen(false);
      setNote(res?.message || `Imported ${parsed.length} record(s).`);
      setOffset(0);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not read or import the template.');
    } finally { setIsPorting(false); }
  };

  if (!specs) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <span className="inline-flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </span>
      </div>
    );
  }
  if (!spec) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 bg-slate-100 dark:bg-slate-900">
        <p className="m-0 text-sm text-slate-500 dark:text-slate-400">Unknown master-data type “{type}”.</p>
        <button onClick={() => navigate('/imports')} className="text-sm text-blue-600 hover:underline">Back to Master Data</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-5 pb-3">
        <button onClick={() => navigate(home.path)}
          className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
          <ArrowLeft className="w-3.5 h-3.5" /> {home.label}
        </button>
        <h1 className="m-0 mt-1 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{spec.label}</h1>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{spec.blurb}</p>
      </div>

      <div className="shrink-0 px-6 pb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder={`Search ${spec.label.toLowerCase()}…`}
            className="w-64 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 pl-8 pr-2.5 py-1.5 outline-none focus:ring-2 focus:ring-slate-400" />
        </div>
        {activeIds.length > 0 && (
          <button type="button" onClick={() => { setFilters({}); setOffset(0); }}
            className="text-xs px-2.5 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20">
            Clear filters ({activeIds.length})
          </button>
        )}

        <button type="button" onClick={() => { setImportErrors([]); setPortOpen(true); }}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
          <ArrowDownToLine className="w-3.5 h-3.5" /> Import / Export
        </button>
        <button type="button" onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
          <Plus className="w-3.5 h-3.5" /> Add {spec.singular}
        </button>
        {note && <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{note}</span>}
        {dirtyCount > 0 && <span className="text-xs text-amber-600 dark:text-amber-400">{dirtyCount} unsaved</span>}
        <button type="button" onClick={save} disabled={!dirtyCount || saving}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white ${
            dirtyCount && !saving ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600 cursor-default'}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>

      {error && (
        <div className="shrink-0 mx-6 mb-3 rounded-md border border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        {loading ? (
          <div className="h-40 flex items-center justify-center text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              <MantleDataGrid
                gridId={`master-${type}`}
                columns={columns}
                rowCount={filtered.length}
                getCellContent={getCellContent}
                onCellEdited={onCellEdited}
                onCellClicked={onCellClicked}
                onHeaderMenuClick={onHeaderMenu}
                hint="Header ▾ filters — filtering and search run on the server, so they cover the whole table, not just this page."
              />
            </div>
            <GridPager
              offset={offset}
              limit={limit}
              rowCount={filtered.length}
              total={pageInfo.total}
              hasMore={pageInfo.hasMore}
              loading={loading}
              onOffset={setOffset}
              onLimit={(n) => { setLimit(n); setOffset(0); }}
            />
          </div>
        )}
      </div>

      <p className="shrink-0 px-6 pb-4 m-0 text-[11px] text-slate-400">
        The code column is fixed once a record exists — other tables reference it.
        Tick boxes and choice columns change on click; text edits inline. Deleting a
        record that is in use is refused, so retire it with the Active flag instead.
      </p>

      {/* Lookup picker for a ref cell, anchored under the cell clicked. */}
      {refPicker && (
        <PickerPopover
          anchor={refPicker}
          options={refOptions[refPicker.colId] || []}
          value={(() => {
            const r = rows.find((x) => x[spec.key] === refPicker.rowKey);
            return r ? valueOf(r, refPicker.colId) : undefined;
          })()}
          onPick={(code) => { patch(refPicker.rowKey, refPicker.colId, code); setRefPicker(null); }}
          onClear={() => { patch(refPicker.rowKey, refPicker.colId, ''); setRefPicker(null); }}
          clearLabel="Clear"
          placeholder="Filter…"
          emptyText={(refOptions[refPicker.colId] || []).length === 0
            ? 'Nothing to choose from yet — add records to that list first.'
            : 'No match.'}
          onClose={() => setRefPicker(null)}
        />
      )}

      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {addOpen && (
        <AddModal spec={spec} existing={rows} refOptions={refOptions} busy={adding}
          onAdd={add} onClose={() => setAddOpen(false)} />
      )}

      {portOpen && (
        <>
          <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileSelected} />
          <PortModal
            spec={spec}
            mode={portMode}
            setMode={setPortMode}
            isPorting={isPorting}
            importErrors={importErrors}
            partial={partialExport}
            rowCount={rows.length}
            total={pageInfo.total}
            onExport={handleExport}
            onImport={() => fileInputRef.current?.click()}
            onClose={() => { if (!isPorting) setPortOpen(false); }}
          />
        </>
      )}
    </div>
  );
};

export default MasterDataPage;
